import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import MapControlPanel from "./MapControlPanel";
import { Feature, LineString, MultiLineString, Position } from "geojson";

import useAuth from "../../../hooks/useAuth";
import {
  deleteProperty,
  fetchProperties,
  setProperty,
} from "../../../redux/slices/property";
import UndoIcon from "@mui/icons-material/Undo";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMatchingFeature, isPropertyInUserAssociation } from "../Map";
import * as turf from "@turf/turf";
import mapboxgl from "mapbox-gl";
import useMapDrawing from "../useMapDrawing";
import { WGS84lalToETRSTM35FINxy } from "../../../utils/coordinateConverter";
import {
  getPropertyFromLonLat,
  getPropertyName,
} from "../../../services/maanmittauslaitosService";
import { Box, Button, Card, IconButton } from "@mui/material";
import PropertyModal from "./addPropertyModal";
import RemoveModal from "./removePropertyModal";
import PointModal, { PointOfInterestData } from "./PointModal";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import MapZoomControls from "./MapZoomControl";
import { geometryToWsg84 } from "../../../utils/road";
import { setRoad } from "../../../redux/slices/road";

const MAPBOX_CONSTANTS = {
  STYLE_URL: "mapbox://styles/mapbox/outdoors-v11",
  INITIAL_ZOOM: 11,
  MML_TILE_URL:
    "https://avoin-karttakuva.maanmittauslaitos.fi/kiinteisto-avoin/tiles/wmts/1.0.0/kiinteistojaotus/default/v3/WGS84_Pseudo-Mercator/{z}/{y}/{x}.pbf?api-key=e73cf967-d0e5-4150-88f0-931689fcbe82",
} as const;

export const MapComponent: React.FC<any> = ({
  road,
  roads,
  properties,
  type,
  handleSubmit,
  centerCoords,
  mapHeight = "70vh",
  candidates,
  onCandidateClick,
}) => {
  const [isInteractionDisabled, setIsInteractionDisabled] = useState(false);
  const disableInteraction = () => setIsInteractionDisabled(true);
  const enableInteraction = () => setIsInteractionDisabled(false);
  const [selectionEnabled, setSelectionEnabled] = useState(true);
  const [basemap, setBasemap] = useState<
    "mapbox" | "mml-maastokartta" | "mml-taustakartta" | "mml-ortokuva"
  >("mapbox");

  const mapDiv = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const [center, setCenter] = useState<[number, number]>([0, 0]);
  const [drawMode, setDrawMode] = useState("simple_select");
  const [highlightedFeatures, setHighlightedFeatures] = useState<any[]>([]);
  const [addModal, setAddModal] = useState(false);
  const [alertModal, setAlertModal] = useState(false);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const mapCreatedRef = useRef(false);
  const candidatesRef = useRef<any[]>([]);
  const [propertyIdToRemove, setPropertyIdToRemove] = useState<any>("");
  const { currentUser } = useAuth();
  const [area, setArea] = useState<Number>(0);
  const [selectedFeature, setSelectedFeature] = useState<any | null>(null);
  const [selectedPropertyName, setSelectedPropertyName] = useState<
    string | null
  >(null);
  const dispatch = useAppDispatch();
  const { users } = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const [addPointModal, setAddPointModal] = useState(false);
  const [pointLocation, setPointLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [markers, setMarkers] = useState<
    {
      id: string;
      coordinates: [number, number];
      markerInstance: mapboxgl.Marker;
    }[]
  >([]);
  const [savedPoints, setSavedPoints] = useState<
    {
      id: string;
      coordinates: [number, number];
      data: any;
    }[]
  >([]);
  const [pointModalMode, setPointModalMode] = useState<
    "create" | "view" | "edit"
  >("create");
  const [selectedPoint, setSelectedPoint] =
    useState<PointOfInterestData | null>(null);
  const [tempMarker, setTempMarker] = useState<mapboxgl.Marker | null>(null);

  const setLayerVisible = (layerId: string, visible: boolean) => {
    if (!map || !map.isStyleLoaded() || !map.getLayer(layerId)) return;
    map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
  };

  const isMounted = useRef(true);
  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  useEffect(() => {
    if (!map) return;

    const mmlLayers = ["mml-maastokartta", "mml-taustakartta", "mml-ortokuva"];
    mmlLayers.forEach((id) => setLayerVisible(id, false));

    if (basemap !== "mapbox") {
      setLayerVisible(basemap, true);
    }
  }, [map, basemap]);

  const [selectedRoadId, setSelectedRoadId] = useState<string | number | null>(
    null
  );
  const [lng, setLng] = useState(0);
  const [lat, setLat] = useState(0);
  const [currentCoordinates, setCurrentCoordinates] = useState<
    Position | Position[] | Position[][] | Position[][][]
  >([]);
  const currentCoordinatesRef = useRef<any>(null);
  const selectedRoadIdRef = useRef<string | number | null>(null);

  const sourceRoad = road || roads?.[0];

  useEffect(() => {
    if (type === "roads") {
      if (
        sourceRoad &&
        sourceRoad.location &&
        sourceRoad.location.coordinates
      ) {
        setLng(sourceRoad.location.coordinates[0]);
        setLat(sourceRoad.location.coordinates[1]);
      }
    }
  }, [sourceRoad, type]);

  useEffect(() => {
    if (type === "roads") {
      currentCoordinatesRef.current = currentCoordinates;
    }
  }, [currentCoordinates, type]);

  useEffect(() => {
    if (type === "roads") {
      const attachMap = (
        setMap: React.Dispatch<React.SetStateAction<mapboxgl.Map | null>>,
        mapDiv: React.RefObject<HTMLDivElement>
      ) => {
        if (!mapDiv.current) return;
        if (mapDiv.current.childNodes.length > 0) {
          mapDiv.current.innerHTML = "";
        }

        const map = new mapboxgl.Map({
          container: mapDiv.current,
          style: "mapbox://styles/mapbox/outdoors-v11",
          center: centerCoords ?? [lng, lat],
          zoom: 9,
        });

        function getNearestLine(
          coordinates: number[][][],
          clickPoint: mapboxgl.LngLat
        ): number[][] {
          let closest = coordinates[0];
          let minDistance = Infinity;

          coordinates.forEach((lineCoords) => {
            lineCoords.forEach(([lon, lat]) => {
              const dx = clickPoint.lng - lon;
              const dy = clickPoint.lat - lat;
              const dist = dx * dx + dy * dy;

              if (dist < minDistance) {
                minDistance = dist;
                closest = lineCoords;
              }
            });
          });

          return closest;
        }

        map.on("load", () => {
          map.addSource("mml-taustakartta", {
            type: "raster",
            tiles: [
              "https://avoin-karttakuva.maanmittauslaitos.fi/avoin/wmts/1.0.0/taustakartta/default/WGS84_Pseudo-Mercator/{z}/{y}/{x}.png?api-key=9396425f-0fe1-4631-adc9-a3f6621fcbcb",
            ],
            tileSize: 256,
            attribution: "© Maanmittauslaitos (NLS) – Maastokartta, CC BY 4.0",
          });

          map.addLayer({
            id: "mml-taustakartta",
            type: "raster",
            source: "mml-taustakartta",
            paint: { "raster-opacity": 1 },
          });

          map.addSource("mml-ortokuva", {
            type: "raster",
            tiles: [
              "https://avoin-karttakuva.maanmittauslaitos.fi/avoin/wmts/1.0.0/ortokuva/default/WGS84_Pseudo-Mercator/{z}/{y}/{x}.png?api-key=9396425f-0fe1-4631-adc9-a3f6621fcbcb",
            ],
            tileSize: 256,
            attribution: "© Maanmittauslaitos (NLS) – Maastokartta, CC BY 4.0",
          });

          map.addLayer({
            id: "mml-ortokuva",
            type: "raster",
            source: "mml-ortokuva",
            paint: { "raster-opacity": 1 },
          });

          map.addSource("mml-maastokartta", {
            type: "raster",
            tiles: [
              "https://avoin-karttakuva.maanmittauslaitos.fi/avoin/wmts/1.0.0/maastokartta/default/WGS84_Pseudo-Mercator/{z}/{y}/{x}.png?api-key=9396425f-0fe1-4631-adc9-a3f6621fcbcb",
            ],
            tileSize: 256,
            attribution: "© Maanmittauslaitos (NLS) – Maastokartta, CC BY 4.0",
          });

          map.addLayer({
            id: "mml-maastokartta",
            type: "raster",
            source: "mml-maastokartta",
            paint: { "raster-opacity": 1 },
          });
          // 🔧 Add this highlight source + layer ONCE
          map.addSource("highlighted-road", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [],
            },
          });

          map.addLayer({
            id: "highlighted-road",
            type: "line",
            source: "highlighted-road",
            paint: {
              "line-color": "#ffa500",
              "line-width": 4,
            },
          });

          if (!road) {
            map.addLayer({
              id: "custom-roads-clickzone",
              type: "line",
              source: {
                type: "vector",
                url: "mapbox://mapbox.mapbox-streets-v8",
              },
              "source-layer": "road",
              layout: {
                "line-join": "round",
                "line-cap": "round",
              },
              paint: {
                "line-color": "transparent",
                "line-width": 15,
              },
            });
          }

          map.addSource("location", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: roads.map((road: any) => ({
                type: "Feature",
                properties: {},
                geometry: road.location,
              })),
            },
          });

          map.addLayer({
            id: "location",
            type: "circle",
            source: "location",
            paint: {
              "circle-color": "red",
            },
          });

          map.addSource("routes", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: roads.map((road: any) => ({
                type: "Feature",
                properties: {},
                geometry: road.geom,
              })),
            },
          });

          map.addLayer({
            id: "routes",
            type: "line",
            source: "routes",
            layout: {
              "line-join": "round",
              "line-cap": "round",
            },
            paint: {
              "line-color": "blue",
              "line-width": 2,
            },
          });

          // 🔧 Only highlight logic changes below
          if (!road) {
            map.on("click", async (e) => {
              const features = map.queryRenderedFeatures(e.point, {
                layers: ["custom-roads-clickzone"],
              });

              if (!features.length) {
                setSelectedRoadId(null);
                selectedRoadIdRef.current = null;
                setCurrentCoordinates([]);
                map.getCanvas().style.cursor = "";
                const popups =
                  document.getElementsByClassName("mapboxgl-popup");
                while (popups.length) popups[0].remove();
                return;
              }

              const feature = features[0];
              const clickedFeatureId = String(
                feature.id || JSON.stringify(feature.geometry)
              );

              if (selectedRoadIdRef.current === clickedFeatureId) {
                setSelectedRoadId(null);
                selectedRoadIdRef.current = null;
                setCurrentCoordinates([]);
                (
                  map.getSource("highlighted-road") as mapboxgl.GeoJSONSource
                ).setData({
                  type: "FeatureCollection",
                  features: [],
                });
                return;
              }

              selectedRoadIdRef.current = clickedFeatureId;
              setSelectedRoadId(clickedFeatureId);

              let coordinates: number[][] | undefined;

              if (
                Array.isArray(currentCoordinatesRef.current) &&
                currentCoordinatesRef.current.length > 0
              ) {
                coordinates = currentCoordinatesRef.current as number[][];
              } else {
                const geometryType = feature.geometry.type;
                if (geometryType === "LineString") {
                  coordinates = (feature.geometry as LineString).coordinates;
                } else if (geometryType === "MultiLineString") {
                  coordinates = getNearestLine(
                    (feature.geometry as MultiLineString).coordinates,
                    e.lngLat
                  );
                } else {
                  console.warn("Tuntematon geometria:", geometryType);
                  return;
                }
              }

              const newHighlight: Feature<LineString> = {
                type: "Feature",
                geometry: {
                  type: "LineString",
                  coordinates: coordinates,
                },
                properties: feature.properties || {},
              };
              (
                map.getSource("highlighted-road") as mapboxgl.GeoJSONSource
              ).setData(newHighlight);

              const clickedLngLat = e.lngLat;
              const updatedCoordinates = WGS84lalToETRSTM35FINxy({
                Lo: clickedLngLat.lng,
                La: clickedLngLat.lat,
              });

              const fetchTransform = async () => {
                const url = `https://avoinapi.vaylapilvi.fi/viitekehysmuunnin/muunna?x=${updatedCoordinates.E}&y=${updatedCoordinates.N}&valihaku=false&palautusarvot=1&palautusarvot=2&palautusarvot=3&palautusarvot=4&palautusarvot=5&palautusarvot=6`;
                try {
                  const res = await fetch(url);
                  return res.ok ? await res.json() : null;
                } catch (err) {
                  console.error("API-virhe:", err);
                  return null;
                }
              };

              const fetchCoordinates = async (data: any) => {
                const props = data?.features?.[0]?.properties;
                if (!props?.katunimi || !props?.kuntanimi) return;
                const url = `https://avoinapi.vaylapilvi.fi/viitekehysmuunnin/muunna?kuntanimi=${props.kuntanimi}&katunimi=${props.katunimi}&valihaku=true&palautusarvot=1&palautusarvot=2&palautusarvot=3&palautusarvot=4&palautusarvot=5&palautusarvot=6`;
                try {
                  const res = await fetch(url);
                  return res.ok ? await res.json() : null;
                } catch (err) {
                  console.error("API-virhe:", err);
                  return null;
                }
              };

              const transformData = await fetchTransform();
              if (!transformData) return;

              const tienimi =
                transformData.features?.[0]?.properties?.katunimi ??
                "Nimetön tie";
              const kuntanimi =
                transformData.features?.[0]?.properties?.kuntanimi ??
                "Tuntematon";
              const newRoadData = {
                name: tienimi,
                municipality: kuntanimi,
                parent:
                  currentUser?.activeAssociation.road.id ??
                  currentUser?.associations[0].road.id,
              };

              dispatch(setRoad(newRoadData));

              const coords = await fetchCoordinates(transformData);
              if (coords?.features?.length > 0) {
                const feature = coords.features[0];
                const modifiedCoordinates = geometryToWsg84(feature);
                const flattened: number[][] = Array.isArray(
                  modifiedCoordinates[0]
                )
                  ? (modifiedCoordinates as number[][][]).flat()
                  : (modifiedCoordinates as number[][]);

                setCurrentCoordinates(flattened);

                const newData: Feature<LineString> = {
                  type: "Feature",
                  geometry: {
                    type: "LineString",
                    coordinates: flattened,
                  },
                  properties: feature.properties || {},
                };

                (
                  map.getSource("highlighted-road") as mapboxgl.GeoJSONSource
                ).setData(newData);
              } else {
                setCurrentCoordinates([]);
              }

              const existsAlready = roads.some(
                (road: any) => road.name === tienimi
              );
              const isUnknownRoad = newRoadData.name === "Nimetön tie";

              let popupContent = `
              <strong>Tie:</strong> ${tienimi}<br/>
              <strong>Kunta:</strong> ${kuntanimi}<br/>
            `;
              if (!existsAlready && !isUnknownRoad) {
                popupContent += `<button class="add-road-button" type="button">Lisää tie järjestelmään</button>`;
              }

              popupRef.current?.remove();

              const popup = new mapboxgl.Popup()
                .setLngLat(clickedLngLat)
                .setHTML(popupContent)
                .addTo(map);

              popupRef.current = popup;

              popup.on("close", () => {
                if (map.getSource("highlighted-road")) {
                  const source = map.getSource(
                    "highlighted-road"
                  ) as mapboxgl.GeoJSONSource;
                  source.setData({
                    type: "FeatureCollection",
                    features: [],
                  });
                }

                setSelectedRoadId(null);
                selectedRoadIdRef.current = null;
                setCurrentCoordinates([]);

                popupRef.current = null;
              });

              // Wait for DOM to attach and set button handler
              setTimeout(() => {
                const button = document.querySelector(".add-road-button");
                if (button instanceof HTMLButtonElement) {
                  button.onclick = () => handleSubmit(newRoadData);
                }
              }, 50);
            });

            map.on("mousemove", (e) => {
              const features = map.queryRenderedFeatures(e.point, {
                layers: ["custom-roads-clickzone"],
              });
              map.getCanvas().style.cursor = features.length ? "pointer" : "";
            });
          }

          setMap(map);
        });
      };

      if (!map) attachMap(setMap, mapDiv);
    }
  }, [
    map,
    lat,
    lng,
    roads,
    selectedRoadId,
    currentUser?.associations,
    currentCoordinates,
  ]);

  useEffect(() => {
    if (type === "properties") {
      return () => {
        isMounted.current = false;
        popupRef.current?.remove();
        popupRef.current = null;
      };
    }
  }, []);

  useEffect(() => {
    if (type === "properties") {
      if (!selectedFeature || !map) return;
      if (drawModeRef.current === "draw_point") {
        popupRef.current?.remove();
        popupRef.current = null;
        clearHighlightedFeatures();
        return;
      }

      const feature = selectedFeature;

      const idFromLookup = isPropertyInUserAssociation(
        feature,
        properties,
        associationId
      );

      const featuresNeededForUpdate = getMatchingFeature(
        feature,
        properties,
        associationId
      );

      const [lng, lat] = turf.center(feature).geometry.coordinates;

      const area = turf.area(feature);
      const roundedAreaM2 = Math.round(area);
      const areaInHectares = area / 10000;
      const roundedHa = Math.round(areaInHectares * 100) / 100;
      setArea(roundedAreaM2);

      const popupContent = `
  <div style="
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    line-height: 1.5;
    padding: 12px;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    max-width: 240px;
  ">
    <div style="margin-bottom: 8px;">
      <strong>Kiinteistötunnus:</strong><br/>
      ${feature.properties.kiinteistotunnuksenEsitysmuoto}
    </div>
    <div style="margin-bottom: 12px;">
      <strong>Pinta-ala:</strong><br/>
      ${roundedHa} ha
    </div>

    <div style="display: flex; flex-direction: column; gap: 8px; margin-top: 10px;">
      ${
        idFromLookup
          ? `
          <button id="edit-button" style="
            padding: 6px 12px;
            background-color: #1976d2;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
          ">Muokkaa</button>
          
          <button id="remove-button" style="
            padding: 6px 12px;
            background-color: #d32f2f;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
          ">Poista tiekunnasta</button>
        `
          : `
          <button id="open-modal-button" style="
            padding: 6px 12px;
            background-color: #2e7d32;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
          ">Lisää alue tiekuntaan</button>
        `
      }
    </div>
  </div>
`;

      popupRef.current?.remove();
      popupRef.current = new mapboxgl.Popup()
        .setLngLat([lng, lat])
        .setHTML(popupContent)
        .addTo(map);

      popupRef.current.on("close", () => {
        if (!isMounted.current) return;

        clearHighlightedFeatures();
        popupRef.current = null;
        setSelectedFeature(null);
      });

      setTimeout(() => {
        const addButton = document.getElementById("open-modal-button");
        const editButton = document.getElementById("edit-button");
        const removeButton = document.getElementById("remove-button");

        if (addButton) {
          addButton.addEventListener("click", () => setAddModal(true));
        }

        if (editButton) {
          editButton.addEventListener("click", () => {
            dispatch(setProperty(featuresNeededForUpdate));
            navigate(`/properties/${idFromLookup}`);
          });
        }

        if (removeButton) {
          removeButton.addEventListener("click", () => {
            if (idFromLookup) {
              setPropertyIdToRemove(idFromLookup);
              setAlertModal(true);
            }
          });
        }
      }, 0);
    }
  }, [selectedFeature, properties]);

  const handleCloseModal = async () => {
    setAddModal(false);
    setAlertModal(false);
    clearHighlightedFeatures();

    popupRef.current?.remove();
    popupRef.current = null;
    setSelectedFeature(null);
    setSelectedPropertyName(null);
    if (associationId) {
      dispatch(fetchProperties(associationId));
    }
  };

  const { metrics, initializeDrawing, drawInstance, addPointerMarker } =
    useMapDrawing(map);
  const highlightedFeaturesRef = useRef(highlightedFeatures);

  const clearHighlightedFeatures = () => {
    setHighlightedFeatures([]);
    if (map && map.getSource("highlighted-multiple")) {
      const source = map.getSource(
        "highlighted-multiple"
      ) as mapboxgl.GeoJSONSource;
      source.setData({
        type: "FeatureCollection",
        features: [],
      });
    }
  };

  useEffect(() => {
    if (type === "properties" || type === "candidateReview") {
      if (roads?.length > 0) {
        setCenter(roads[0].location.coordinates);
      }
    }
  }, [roads]);

  useEffect(() => {
    candidatesRef.current = candidates ?? [];
  }, [candidates]);

  const drawModeRef = useRef(drawMode);
  useEffect(() => {
    drawModeRef.current = drawMode;
  }, [drawMode]);

  useEffect(() => {
    if (type === "properties") {
      if (!map?.isStyleLoaded()) return;

      const layers = map.getStyle().layers || [];
      layers.forEach((layer) => {
        if (layer.id.startsWith("property")) {
          if (map.getLayer(layer.id)) map.removeLayer(layer.id);
          if (map.getSource(layer.id)) map.removeSource(layer.id);
        }
      });

      properties.forEach((property: any, index: number) => {
        if (property.propertyGeometries) {
          property.propertyGeometries.forEach((geom: any, i: number) => {
            const sourceId = `property${index}-${i}`;
            if (map.getSource(sourceId)) return;

            map.addSource(sourceId, {
              type: "geojson",
              data: {
                type: "Feature",
                properties: {},
                geometry: geom.geom,
              },
            });

            map.addLayer({
              id: sourceId,
              type: "fill",
              source: sourceId,
              paint: {
                "fill-color": "black",
                "fill-opacity": 0.3,
              },
            });
          });
        }
      });
    }
  }, [map, properties]);

  useEffect(() => {
    if (type === "properties") {
      highlightedFeaturesRef.current = highlightedFeatures;
    }
  }, [highlightedFeatures]);

  // ── Candidate review layer ──────────────────────────────────────────────────
  useEffect(() => {
    if (type !== "candidateReview" || !map || !candidates?.length) return;

    const addCandidateLayer = () => {
      const geojson: GeoJSON.FeatureCollection = {
        type: "FeatureCollection",
        features: candidates
          .filter((c: any) => c.geom)
          .map((c: any) => ({
            type: "Feature",
            id: c.id,
            geometry: c.geom,
            properties: {
              candidateId: c.id,
              confidence: c.confidence,
              status: c.status,
            },
          })),
      };

      if (map.getSource("candidates")) {
        (map.getSource("candidates") as mapboxgl.GeoJSONSource).setData(
          geojson
        );
        return;
      }

      map.addSource("candidates", { type: "geojson", data: geojson });

      map.addLayer({
        id: "candidates-fill",
        type: "fill",
        source: "candidates",
        paint: {
          "fill-color": [
            "case",
            ["==", ["get", "status"], "APPROVED"],
            "#2e7d32",
            ["==", ["get", "status"], "REJECTED"],
            "#9e9e9e",
            ["==", ["get", "confidence"], "HIGH"],
            "#66bb6a",
            ["==", ["get", "confidence"], "MEDIUM"],
            "#ffc107",
            "#90caf9",
          ],
          "fill-opacity": [
            "case",
            ["==", ["get", "status"], "REJECTED"],
            0.2,
            0.45,
          ],
        },
      });

      map.addLayer({
        id: "candidates-outline",
        type: "line",
        source: "candidates",
        paint: {
          "line-color": [
            "case",
            ["==", ["get", "status"], "APPROVED"],
            "#1b5e20",
            ["==", ["get", "status"], "REJECTED"],
            "#757575",
            ["==", ["get", "confidence"], "HIGH"],
            "#388e3c",
            ["==", ["get", "confidence"], "MEDIUM"],
            "#f57c00",
            "#1976d2",
          ],
          "line-width": 1.5,
        },
      });
    };

    if (map.isStyleLoaded()) {
      addCandidateLayer();
    } else {
      map.once("load", addCandidateLayer);
    }
  }, [map, type, candidates]);

  // ── Fit map to candidates when they load ──────────────────────────────────
  useEffect(() => {
    if (type !== "candidateReview" || !map || !candidates?.length) return;
    const withGeom = candidates.filter((c: any) => c.geom);
    if (!withGeom.length) return;
    const collection = turf.featureCollection(
      withGeom.map((c: any) => turf.feature(c.geom))
    );
    const bbox = turf.bbox(collection) as [number, number, number, number];
    map.fitBounds(
      [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]],
      ],
      {
        padding: { top: 60, bottom: 60, left: 360, right: 60 },
        maxZoom: 16,
        duration: 800,
      }
    );
  }, [map, type, candidates]);

  // ── Candidate click handler ────────────────────────────────────────────────
  useEffect(() => {
    if (type !== "candidateReview" || !map || !onCandidateClick) return;

    const handleCandidateClick = (e: mapboxgl.MapMouseEvent) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ["candidates-fill"],
      });
      if (features.length > 0) {
        const candidateId = features[0].properties?.candidateId;
        if (candidateId != null) onCandidateClick(Number(candidateId));
      }
    };

    map.on("click", handleCandidateClick);
    map.getCanvas().style.cursor = "pointer";

    return () => {
      map.off("click", handleCandidateClick);
      if (map.getCanvas()) map.getCanvas().style.cursor = "";
    };
  }, [map, type, onCandidateClick]);

  useEffect(() => {
    if (type === "properties") {
      if (!map || !drawInstance) return;

      const handleClick = async (e: mapboxgl.MapMouseEvent) => {
        if (isInteractionDisabled) return;

        const currentMode = drawModeRef.current;
        if (
          isInteractionDisabled ||
          currentMode === "draw_point" ||
          currentMode.startsWith("draw_")
        ) {
          return;
        }

        const { lng, lat } = e.lngLat;
        const clickedPoint = e.point;

        if (map.getLayer("highlighted-multiple")) {
          const clickedFeatures = map.queryRenderedFeatures(clickedPoint, {
            layers: ["highlighted-multiple"],
          });

          if (clickedFeatures.length > 0) {
            const featureId = clickedFeatures[0].id;
            const updatedFeatures = highlightedFeaturesRef.current.filter(
              (f) => f.id !== featureId
            );
            setHighlightedFeatures(updatedFeatures);

            const source = map.getSource(
              "highlighted-multiple"
            ) as mapboxgl.GeoJSONSource;
            source.setData({
              type: "FeatureCollection",
              features: updatedFeatures.map((f) => ({
                type: "Feature",
                geometry: f.geometry,
                properties: {},
                id: f.id,
              })),
            });

            return;
          }
        }

        const finLatLon = await WGS84lalToETRSTM35FINxy({ La: lat, Lo: lng });
        const [property, propertyName] = await Promise.all([
          getPropertyFromLonLat(finLatLon.N, finLatLon.E),
          getPropertyName(finLatLon.E, finLatLon.N),
        ]);
        setSelectedPropertyName(propertyName);

        if (property?.data?.features?.length > 0) {
          const feature = property.data.features[0];
          const geometry = feature.geometry;
          const featureId = feature.id || `${geometry.coordinates.join(",")}`;

          const newFeatures = [
            ...highlightedFeaturesRef.current,
            { id: featureId, geometry },
          ];
          setHighlightedFeatures(newFeatures);
          setSelectedFeature(feature);

          const sourceExists = map.getSource("highlighted-multiple");

          if (sourceExists) {
            (sourceExists as mapboxgl.GeoJSONSource).setData({
              type: "FeatureCollection",
              features: newFeatures.map((f) => ({
                type: "Feature",
                geometry: f.geometry,
                properties: {},
                id: f.id,
              })),
            });
          } else {
            map.addSource("highlighted-multiple", {
              type: "geojson",
              data: {
                type: "FeatureCollection",
                features: newFeatures.map((f) => ({
                  type: "Feature",
                  geometry: f.geometry,
                  properties: {},
                  id: f.id,
                })),
              },
            });

            map.addLayer(
              {
                id: "highlighted-multiple",
                type: "fill",
                source: "highlighted-multiple",
                paint: {
                  "fill-color": "#ff7800",
                  "fill-opacity": 0.6,
                },
              },
              "kiinteistorajat"
            );
          }
        }
      };
      const handleSelectionChange = (e: any) => {
        if (!selectionEnabled) {
          return;
        }
      };

      map.on("click", handleClick);
      map.on("draw.selectionchange", handleSelectionChange);

      return () => {
        map.off("click", handleClick);
        map.off("draw.selectionchange", handleSelectionChange);
      };
    }
  }, [map, drawInstance, selectionEnabled]);

  useEffect(() => {
    if (!map) return;

    markers.forEach((m) => m.markerInstance.remove());

    const newMarkers: typeof markers = [];

    savedPoints.forEach((point) => {
      const markerInstance = new mapboxgl.Marker()
        .setLngLat(point.coordinates)
        .addTo(map);

      markerInstance.getElement().style.pointerEvents = "auto";
      markerInstance.getElement().addEventListener("click", () => {
        setSelectedPoint(point.data);
        setPointLocation({
          lat: point.coordinates[1],
          lng: point.coordinates[0],
        });
        setPointModalMode("view");
        setAddPointModal(true);
      });

      newMarkers.push({
        id: point.id,
        coordinates: point.coordinates,
        markerInstance,
      });
    });

    setMarkers(newMarkers);
  }, [map, savedPoints]);

  useEffect(() => {
    if (type === "properties" || type === "candidateReview") {
      if (!mapDiv.current || mapCreatedRef.current) return;
      mapCreatedRef.current = true;

      const newMap = new mapboxgl.Map({
        container: mapDiv.current,
        style: MAPBOX_CONSTANTS.STYLE_URL,
        center,
        zoom: MAPBOX_CONSTANTS.INITIAL_ZOOM,
      });

      newMap.on("load", () => {
        newMap.addSource("mml-taustakartta", {
          type: "raster",
          tiles: [
            "https://avoin-karttakuva.maanmittauslaitos.fi/avoin/wmts/1.0.0/taustakartta/default/WGS84_Pseudo-Mercator/{z}/{y}/{x}.png?api-key=9396425f-0fe1-4631-adc9-a3f6621fcbcb",
          ],
          tileSize: 256,
          attribution: "© Maanmittauslaitos (NLS) – Maastokartta, CC BY 4.0",
        });

        newMap.addLayer({
          id: "mml-taustakartta",
          type: "raster",
          source: "mml-taustakartta",
          layout: { visibility: "none" },
          paint: { "raster-opacity": 1 },
        });

        newMap.addSource("mml-ortokuva", {
          type: "raster",
          tiles: [
            "https://avoin-karttakuva.maanmittauslaitos.fi/avoin/wmts/1.0.0/ortokuva/default/WGS84_Pseudo-Mercator/{z}/{y}/{x}.png?api-key=9396425f-0fe1-4631-adc9-a3f6621fcbcb",
          ],
          tileSize: 256,
          attribution: "© Maanmittauslaitos (NLS) – Maastokartta, CC BY 4.0",
        });

        newMap.addLayer({
          id: "mml-ortokuva",
          type: "raster",
          source: "mml-ortokuva",
          layout: { visibility: "none" },
          paint: { "raster-opacity": 1 },
        });

        newMap.addSource("mml-maastokartta", {
          type: "raster",
          tiles: [
            "https://avoin-karttakuva.maanmittauslaitos.fi/avoin/wmts/1.0.0/maastokartta/default/WGS84_Pseudo-Mercator/{z}/{y}/{x}.png?api-key=9396425f-0fe1-4631-adc9-a3f6621fcbcb",
          ],
          tileSize: 256,
          attribution: "© Maanmittauslaitos (NLS) – Maastokartta, CC BY 4.0",
        });

        newMap.addLayer({
          id: "mml-maastokartta",
          type: "raster",
          source: "mml-maastokartta",
          layout: { visibility: "none" },
          paint: { "raster-opacity": 1 },
        });

        newMap.addSource("pbfTiles", {
          type: "vector",
          tiles: [MAPBOX_CONSTANTS.MML_TILE_URL],
          minzoom: 10,
          maxzoom: 16,
        });

        newMap.addLayer({
          id: "kiinteistorajat",
          type: "line",
          source: "pbfTiles",
          "source-layer": "KiinteistorajanSijaintitiedot",
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#888",
            "line-width": 2,
          },
        });

        newMap.addLayer({
          id: "rajamerkki",
          type: "circle",
          source: "pbfTiles",
          "source-layer": "RajamerkinSijaintitiedot",
          paint: {
            "circle-radius": 3,
            "circle-color": "red",
          },
        });
        newMap.addLayer({
          id: "text-layer",
          type: "symbol",
          source: "pbfTiles",
          "source-layer": "KiinteistotunnuksenSijaintitiedot",
          layout: {
            "text-field": ["get", "kiinteistotunnuksenEsitysmuoto"],
            "text-size": 15,
            "text-anchor": "top",
            "text-offset": [0, 1],
          },
          paint: {
            "text-color": "#000000",
          },
        });

        if (properties && Array.isArray(properties)) {
          properties.forEach((property: any, index: number) => {
            if (property.propertyGeometries) {
              property.propertyGeometries.forEach((geom: any, i: number) => {
                newMap.addSource(`property${index}-${i}`, {
                  type: "geojson",
                  data: {
                    type: "Feature",
                    properties: {},
                    geometry: geom.geom,
                  },
                });

                newMap.addLayer({
                  id: `property${index}-${i}`,
                  type: "fill",
                  source: `property${index}-${i}`,
                  paint: {
                    "fill-color": "black",
                    "fill-opacity": 0.3,
                  },
                });
              });
            }
          });
        }

        if (roads && Array.isArray(roads)) {
          roads.forEach((road: any, index: any) => {
            if (road.location) {
              newMap.addSource(`location${index}`, {
                type: "geojson",
                data: {
                  type: "Feature",
                  properties: {},
                  geometry: road.location,
                },
              });

              newMap.addLayer({
                id: `location${index}`,
                type: "circle",
                source: `location${index}`,
                paint: {
                  "circle-color": "red",
                },
              });
            }

            newMap.addSource(`route${index}`, {
              type: "geojson",
              data: {
                type: "Feature",
                properties: {},
                geometry: road.geom,
              },
            });

            newMap.addLayer({
              id: `route${index}`,
              type: "line",
              source: `route${index}`,
              layout: {
                "line-join": "round",
                "line-cap": "round",
              },
              paint: {
                "line-color": "blue",
                "line-width": 2,
              },
            });
          });
        }

        if (type === "candidateReview" && candidatesRef.current?.length > 0) {
          const cands = candidatesRef.current;
          const geojson: GeoJSON.FeatureCollection = {
            type: "FeatureCollection",
            features: cands
              .filter((c: any) => c.geom)
              .map((c: any) => ({
                type: "Feature",
                id: c.id,
                geometry: c.geom,
                properties: {
                  candidateId: c.id,
                  confidence: c.confidence,
                  status: c.status,
                },
              })),
          };
          newMap.addSource("candidates", { type: "geojson", data: geojson });
          newMap.addLayer({
            id: "candidates-fill",
            type: "fill",
            source: "candidates",
            paint: {
              "fill-color": [
                "case",
                ["==", ["get", "status"], "APPROVED"],
                "#2e7d32",
                ["==", ["get", "status"], "REJECTED"],
                "#9e9e9e",
                ["==", ["get", "confidence"], "HIGH"],
                "#66bb6a",
                ["==", ["get", "confidence"], "MEDIUM"],
                "#ffc107",
                "#90caf9",
              ],
              "fill-opacity": [
                "case",
                ["==", ["get", "status"], "REJECTED"],
                0.2,
                0.45,
              ],
            },
          });
          newMap.addLayer({
            id: "candidates-outline",
            type: "line",
            source: "candidates",
            paint: {
              "line-color": [
                "case",
                ["==", ["get", "status"], "APPROVED"],
                "#1b5e20",
                ["==", ["get", "status"], "REJECTED"],
                "#757575",
                ["==", ["get", "confidence"], "HIGH"],
                "#388e3c",
                ["==", ["get", "confidence"], "MEDIUM"],
                "#f57c00",
                "#1976d2",
              ],
              "line-width": 1.5,
            },
          });
        }

        setMap(newMap);
      });

      return () => {
        newMap.remove();
        setMap(null);
        mapCreatedRef.current = false;
      };
    }
  }, [center, roads]);

  useEffect(() => {
    if (map) {
      initializeDrawing();
    }
  }, [map, initializeDrawing]);

  const toggleSelection = useCallback(() => {
    if (type === "properties") {
      if (!drawInstance || !map) return;

      const selected = drawInstance.getSelectedIds();
      const selectedId = selected[0];

      if (selectionEnabled) {
        if (selectedId) {
          drawInstance.changeMode("direct_select", { featureId: selectedId });
        } else {
          console.warn("No feature selected for direct_select.");
        }
        disableInteraction();
      } else {
        drawInstance.changeMode("simple_select");
        enableInteraction();
      }

      setSelectionEnabled(!selectionEnabled);
    }
  }, [drawInstance, selectionEnabled, map]);

  const handleDeletePoint = (point: PointOfInterestData | null) => {
    if (!point) return;

    setSavedPoints((prev) =>
      prev.filter(
        (p) =>
          !(p.coordinates[0] === point.lng && p.coordinates[1] === point.lat)
      )
    );

    setMarkers((prev) => {
      const markerToRemove = prev.find(
        (m) => m.coordinates[0] === point.lng && m.coordinates[1] === point.lat
      );
      if (markerToRemove) {
        markerToRemove.markerInstance.remove();
      }
      return prev.filter(
        (m) =>
          !(m.coordinates[0] === point.lng && m.coordinates[1] === point.lat)
      );
    });

    setSelectedPoint(null);
    setPointLocation(null);
    setAddPointModal(false);
  };

  useEffect(() => {
    if (!map || !drawInstance) return;

    const handleCreate = (e: any) => {
      const feature = e.features[0];
      if (feature.geometry.type === "Point") {
        const [lng, lat] = feature.geometry.coordinates;
        const markerInstance = addPointerMarker([lng, lat]);

        if (markerInstance) {
          markerInstance.getElement().style.pointerEvents = "auto";
          markerInstance.getElement().addEventListener("click", () => {
            const found = savedPoints.find(
              (p) => p.coordinates[0] === lng && p.coordinates[1] === lat
            );
            if (found) {
              setSelectedPoint(found.data);
              setPointModalMode("view");
              setAddPointModal(true);
            }
          });
          setTempMarker(markerInstance);

          const markerId = crypto.randomUUID();
          setMarkers((prev) => [
            ...prev,
            {
              id: markerId,
              coordinates: [lng, lat],
              markerInstance,
            },
          ]);
          setPointLocation({ lat, lng });
          setPointModalMode("create");
          setSelectedPoint(null);
          setAddPointModal(true);
        }
        drawInstance?.delete(feature.id);
      }
    };

    map.on("draw.create", handleCreate);

    return () => {
      map.off("draw.create", handleCreate);
    };
  }, [map, drawInstance, addPointerMarker]);

  const handleAddPointSubmit = (data: PointOfInterestData) => {
    setSavedPoints((prev) => {
      const index = prev.findIndex(
        (p) => p.coordinates[0] === data.lng && p.coordinates[1] === data.lat
      );

      if (index !== -1) {
        const updated = [...prev];
        updated[index] = { ...updated[index], data };
        return updated;
      }
      return [
        ...prev,
        {
          id: crypto.randomUUID(),
          coordinates: [data.lng, data.lat],
          data,
        },
      ];
    });
    if (tempMarker) {
      setTempMarker(null);
    }

    setAddPointModal(false);
    setSelectedPoint(null);
    setPointLocation(null);
  };

  useEffect(() => {
    if (!map || !drawInstance) return;
    const handleModeChange = (e: any) => {
      drawModeRef.current = e.mode;
      setDrawMode(e.mode);
    };
    map.on("draw.modechange", handleModeChange);
    return () => {
      map.off("draw.modechange", handleModeChange);
    };
  }, [map, drawInstance]);

  const handleZoomIn = () => {
    if (map) {
      map.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (map) {
      map.zoomOut();
    }
  };

  return (
    <Card
      style={{
        height: mapHeight,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ flexGrow: 1, display: "flex", position: "relative" }}>
        <MapControlPanel
          onDrawLine={() => {
            drawInstance?.changeMode("draw_line_string");
            drawModeRef.current = "draw_line_string";
            setDrawMode("draw_line_string");
          }}
          onDrawPolygon={() => {
            drawInstance?.changeMode("draw_polygon");
            drawModeRef.current = "draw_polygon";
            setDrawMode("draw_polygon");
          }}
          onDrawPoint={() => {
            drawInstance?.changeMode("draw_point");
            drawModeRef.current = "draw_point";
            setDrawMode("draw_point");
          }}
          onStopDrawing={() => {
            drawInstance?.changeMode("simple_select");
            drawModeRef.current = "simple_select";
            setDrawMode("simple_select");
          }}
          onDeleteFeature={() => {
            const selected = drawInstance?.getSelectedIds();
            if (selected && selected.length > 0) {
              drawInstance?.delete(selected);
            }
          }}
          onToggleSelection={toggleSelection}
          isDrawing={drawMode !== "simple_select"}
          isSelectionEnabled={selectionEnabled}
          basemap={basemap}
          onChangeBasemap={setBasemap}
        />
        <div ref={mapDiv} className="map-container" style={{ flexGrow: 1 }} />
        <MapZoomControls onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />

        {addModal && selectedFeature && (
          <PropertyModal
            key={selectedFeature.id}
            show={addModal}
            onClose={handleCloseModal}
            property={selectedFeature}
            currentUser={currentUser}
            users={users}
            properties={properties}
            area={area}
            initialName={selectedPropertyName ?? undefined}
          />
        )}
        {alertModal && (
          <RemoveModal
            open={true}
            onClose={handleCloseModal}
            propertyIdToRemove={propertyIdToRemove}
            onConfirm={async () => {
              await dispatch(deleteProperty(propertyIdToRemove));
              await handleCloseModal();
            }}
          />
        )}
        {addPointModal && pointLocation && (
          <PointModal
            open={addPointModal}
            onClose={() => {
              if (tempMarker) {
                tempMarker.remove();
                setTempMarker(null);
              }

              setAddPointModal(false);
              setSelectedPoint(null);
              setPointLocation(null);
            }}
            location={pointLocation}
            mode={pointModalMode}
            data={selectedPoint || undefined}
            onSubmit={handleAddPointSubmit}
            onDelete={() => handleDeletePoint(selectedPoint)}
          />
        )}
      </div>
    </Card>
  );
};
